/* CanvasToolbar — Top bar with name, save, active toggle, test run */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import css from '../AutomationEditor.module.css';
import { useUpdateAutomation, useToggleAutomation } from '../../../hooks/useAutomations';
import { useToast } from '../../../hooks/useToast';
import type { AutomationDetail, ValidationResult } from '../../../types/automations';

interface Props {
  automation: AutomationDetail;
  isSaving: boolean;
  isDirty: boolean;
  onSave: () => void;
  onValidate: () => Promise<ValidationResult | null>;
  onTestRun: () => void;
}

export default function CanvasToolbar({
  automation,
  isSaving,
  isDirty,
  onSave,
  onValidate,
  onTestRun,
}: Props) {
  const navigate = useNavigate();
  const updateAutomation = useUpdateAutomation();
  const toggleAutomation = useToggleAutomation();
  const { showToast } = useToast();
  const [name, setName] = useState(automation.name);

  const handleNameBlur = useCallback(async () => {
    if (name !== automation.name && name.trim()) {
      try {
        await updateAutomation.mutateAsync({ id: automation.id, name: name.trim() });
      } catch {
        showToast('Failed to update name', 'error');
        setName(automation.name);
      }
    }
  }, [name, automation.id, automation.name, updateAutomation, showToast]);

  const handleToggle = useCallback(async () => {
    // If activating, validate first
    if (!automation.is_active) {
      const validation = await onValidate();
      if (validation && !validation.valid) {
        showToast('Cannot activate — fix validation errors first', 'error');
        return;
      }
    }

    try {
      const result = await toggleAutomation.mutateAsync(automation.id);
      showToast(`Automation ${result.is_active ? 'activated' : 'deactivated'}`, 'success');
    } catch {
      showToast('Failed to toggle', 'error');
    }
  }, [automation.id, automation.is_active, toggleAutomation, showToast, onValidate]);

  return (
    <div className={css.toolbar}>
      <div className={css.toolbarLeft}>
        <button
          className={css.backBtn}
          onClick={() => {
            if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
            navigate('/admin/automations');
          }}
        >
          ← Back
        </button>
        <input
          className={css.automationName}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </div>

      <div className={css.toolbarRight}>
        <button className={css.testBtn} onClick={onTestRun} title="Test run with mock data">
          🧪 Test
        </button>
        <div className={css.activeToggle}>
          <button
            className={css.toggleSwitch}
            data-active={automation.is_active}
            onClick={handleToggle}
            title={automation.is_active ? 'Deactivate' : 'Activate'}
          />
          <span>{automation.is_active ? 'Active' : 'Inactive'}</span>
        </div>
        <button className={css.saveBtn} onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
